import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, Truck } from "lucide-react";

const API_BASE = api.defaults.baseURL;

async function downloadChallanPdf(id, code) {
  try {
    const res = await fetch(`${API_BASE}/exports/challan/${id}/pdf`, { credentials: "include" });
    if (!res.ok) { toast.error(`Download failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code.replace(/\//g, "_")}-challan.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}

export default function CustomerChallans() {
  const { data } = useQuery({
    queryKey: ["my-challans"],
    queryFn: () => api.get("/accounts/challans").then((r) => r.data),
  });
  const items = data?.items || [];
  const totalLitres = items.reduce((s, c) => s + (Number(c.quantity_litres) || 0), 0);

  return (
    <div className="space-y-4" data-testid="customer-challans-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">My Delivery Challans</h1>
        <p className="text-sm text-muted-foreground">Every delivery gets a challan — your official proof of receipt with quantity, bowser & driver details.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div className="kpi-card">
          <div className="flex items-center justify-between text-muted-foreground">
            <div className="text-xs uppercase tracking-widest">Deliveries</div>
            <FileText size={14} />
          </div>
          <div className="mt-2 font-display text-xl font-black">{items.length}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center justify-between text-muted-foreground">
            <div className="text-xs uppercase tracking-widest">Total litres received</div>
            <Truck size={14} />
          </div>
          <div className="mt-2 font-display text-xl font-black">{totalLitres.toLocaleString("en-IN")} L</div>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>All challans</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Challan No.</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2 text-right">Qty (L)</th>
                <th className="px-4 py-2 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-border/40 text-sm" data-testid={`my-challan-row-${c.code}`}>
                  <td className="px-4 py-2 font-mono">{c.code}</td>
                  <td className="px-4 py-2 text-xs">{c.date?.slice(0, 10)}</td>
                  <td className="px-4 py-2">
                    <Link to={`/portal/track/${c.order_id}`} className="font-mono text-primary hover:underline">{c.order_code}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{c.bowser_code || "—"}</td>
                  <td className="px-4 py-2 text-xs">{c.driver_name || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{Number(c.quantity_litres).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadChallanPdf(c.id, c.code)} data-testid={`my-challan-dl-${c.code}`}>
                      <Download size={12} className="mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <FileText size={32} className="mx-auto mb-2 opacity-40" />
                  No deliveries yet. Book your first order to get a delivery challan.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
